import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';

/**
 * OIDC Authorization consent page.
 * Nextcloud's user_oidc redirects the browser here with query params:
 *   client_id, redirect_uri, state, scope, response_type, nonce
 *
 * If the user is already authenticated in our app (JWT in localStorage), we
 * auto-approve by POSTing to /api/oidc/approve and then redirecting the
 * browser back to Nextcloud's redirect_uri with ?code=...&state=...
 *
 * If the user is NOT authenticated, we redirect them to /login and preserve
 * the originally requested authorize URL so they come back here after login.
 */
export default function OidcAuthorizePage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('processing'); // processing | error
    const [message, setMessage] = useState('Mengarahkan ke Nextcloud...');
    // Guard against React 18 StrictMode double-invocation which would otherwise
    // overwrite post_login_redirect with '/login' after the first navigate().
    const didRunRef = useRef(false);

    useEffect(() => {
        if (didRunRef.current) return;
        didRunRef.current = true;

        const clientId = params.get('client_id');
        const redirectUri = params.get('redirect_uri');
        const state = params.get('state') || '';
        const scope = params.get('scope') || 'openid profile email';
        const responseType = params.get('response_type') || 'code';
        const nonce = params.get('nonce');

        if (!clientId || !redirectUri) {
            setStatus('error');
            setMessage('Parameter OIDC tidak lengkap');
            return;
        }
        if (responseType !== 'code') {
            setStatus('error');
            setMessage('response_type tidak didukung (hanya "code")');
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            // Preserve the authorize URL (built from params, not window.location
            // which may have already changed under StrictMode) so user returns
            // here after login.
            const qs = params.toString();
            const redirectBack = qs ? `/oidc/authorize?${qs}` : '/oidc/authorize';
            localStorage.setItem('post_login_redirect', redirectBack);
            navigate('/login');
            return;
        }

        (async () => {
            try {
                const res = await api.post('/oidc/approve', {
                    client_id: clientId,
                    redirect_uri: redirectUri,
                    state,
                    scope,
                    nonce,
                    response_type: responseType,
                });
                const code = res.data?.code;
                if (!code) throw new Error('No code issued');
                const url = new URL(redirectUri);
                url.searchParams.set('code', code);
                if (state) url.searchParams.set('state', state);
                // Full redirect (not SPA nav) so we leave our origin cleanly
                window.location.replace(url.toString());
            } catch (err) {
                setStatus('error');
                const detail = err.response?.data?.detail || err.message || 'Gagal memproses OIDC';
                setMessage(typeof detail === 'string' ? detail : 'Gagal memproses OIDC');
            }
        })();
    }, [params, navigate]);

    return (
        <div className="flex items-center justify-center min-h-screen bg-brand-bg-primary">
            <div className="bg-white border border-brand-border rounded-lg p-8 max-w-md w-full shadow-card text-center">
                {status === 'processing' ? (
                    <>
                        <Loader2 className="w-10 h-10 text-brand-primary animate-spin mx-auto mb-4" />
                        <p className="text-brand-text-primary font-semibold">{message}</p>
                        <p className="text-sm text-brand-text-secondary mt-2">
                            Mengarahkan Anda kembali ke Nextcloud...
                        </p>
                    </>
                ) : (
                    <>
                        <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
                        <p className="text-red-600 font-semibold">Autentikasi Gagal</p>
                        <p className="text-sm text-brand-text-secondary mt-2 mb-4">{message}</p>
                        <Button onClick={() => navigate('/')}>
                            <LogIn className="w-4 h-4 mr-2" /> Kembali ke Beranda
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
